import { useForm } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/Button';
import { TextInput } from '../../components/TextInput';
import './LoginScreen.css';
import {RosterPilotLogo} from '../../assets/RosterPilotLogo.tsx';
import { returnPathFrom } from '../../utils/returnPath';
import { isUnlinkedAccount } from '../../utils/profileMissing';
import { useTravelBallAvailability } from '../../hooks/useTravelBallAvailability';

type LoginFormData = {
  email: string;
  password: string;
};

export const LoginScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, logout, loading, user } = useAuth();
  // The travel ball signup link shows only once the API says travel ball is open (fails closed).
  const travelBallAvailable = useTravelBallAvailability();
  const [apiError, setApiError] = useState<string>('');
  /**
   * The account signed in to Firebase but has no active RosterPilot user, and no signup under way in
   * this browser: most often staff an admin removed. Shown with a way to start a new program, since
   * the account can do nothing else here.
   */
  const [unlinked, setUnlinked] = useState(false);
  const signedOutUidRef = useRef<string | null>(null);

  /**
   * Such a session is signed out here (the routes send it to /login rather than into signup or the
   * app), through AuthContext so per-user caches are cleared too. Once per account: AuthContext's
   * `logout` is a new function every render, and a failed sign-out must not be retried in a loop.
   */
  useEffect(() => {
    if (!user || !isUnlinkedAccount(user) || signedOutUidRef.current === user.uid) return;
    signedOutUidRef.current = user.uid;
    void logout()
      .catch(() => {
        // Still signed in: the message stays up (it is derived from the user below), and the
        // sign-in form replaces the session on the next attempt.
      })
      .finally(() => setUnlinked(true));
  }, [user, logout]);

  const showUnlinked = unlinked || isUnlinkedAccount(user);

  /**
   * Prefilled after accepting an invitation, which creates the account without issuing a session and
   * then sends the new coach here.
   *
   * Carried in router state rather than the query string: `index.html` loads Google Fonts on every
   * route, so an address in the URL would be handed to a third party in a `Referer` header.
   */
  const prefilledEmail = (location.state as { email?: unknown } | null)?.email;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    mode: 'onSubmit',
    defaultValues: {
      email: typeof prefilledEmail === 'string' ? prefilledEmail : '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    setApiError(''); // Clear any previous errors
    setUnlinked(false);

    try {
      await login(data);
      // Phase 1b.5: `/` runs through AuthLandingRoute, which reads the active view and sends
      // college users to `/players` and TB users to their own home. A guarded page's return
      // path still wins (e.g. Stripe's `/signup/complete`). An unpaid self-serve admin is still
      // routed on to `/signup/payment` by ProtectedRoute.
      navigate(returnPathFrom(location.state) ?? '/');
    } catch (error: unknown) {
      const authError = error as { code?: string; message?: string };
      let errorMessage = 'An error occurred during login';

      // Map Firebase error codes to user-friendly messages
      switch (authError.code) {
        case 'auth/invalid-credential':
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          errorMessage = 'Invalid email or password';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed attempts. Please try again later';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your connection';
          break;
        case 'auth/configuration-not-found':
        case 'auth/invalid-api-key':
          errorMessage = 'Firebase is not configured properly. Please check your environment variables.';
          break;
        case 'auth/unauthorized':
          // Firebase accepted the password but there is no RosterPilot user for it. The effect above
          // signs the session out; this shows why straight away.
          setUnlinked(true);
          return;
        case 'auth/profile-fetch-failed':
          errorMessage = 'Login successful, but failed to fetch user profile. Please try again.';
          break;
        default:
          errorMessage = authError.message || errorMessage;
      }

      setApiError(errorMessage);
    }
  };

  const handleResetPassword = () => {
    navigate('/forgot-password');
  };

  return (
    <div className="login-screen">
      <div className="login-container">
        <div className="login-logo">
          <RosterPilotLogo />
        </div>

        <div className="login-header">
          <h1 className="login-title">Log In</h1>
          <p className="login-subtitle">Scout And Track Players With RosterPilot.</p>
        </div>

        <form className="login-form" onSubmit={handleSubmit(onSubmit)}>
          {showUnlinked ? (
            <div className="login-error-banner" role="alert" data-testid="login-unlinked">
              This email isn&apos;t linked to an active RosterPilot account. Ask your program&apos;s
              admin for access, or{' '}
              <Link to="/signup" className="login-forgot-link">
                start a new program
              </Link>
              .
            </div>
          ) : (
            apiError && <div className="login-error-banner">{apiError}</div>
          )}

          <div className="login-inputs">
            <TextInput
              label="Email Address"
              type="email"
              placeholder="email"
              autoComplete="username"
              error={errors.email?.message}
              disabled={loading}
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: 'Email is invalid',
                },
              })}
            />

            <TextInput
              label="Password"
              type="password"
              placeholder="******"
              autoComplete="current-password"
              error={errors.password?.message}
              disabled={loading}
              {...register('password', {
                required: 'Password is required',
                minLength: {
                  value: 6,
                  message: 'Password must be at least 6 characters',
                },
              })}
            />
          </div>

          <div className="login-actions">
            <Button type="submit" fullWidth loading={loading}>
              Log In
            </Button>

            <div className="login-forgot">
              <span className="login-forgot-text">Forgot password? </span>
              <button
                type="button"
                className="login-forgot-link"
                onClick={handleResetPassword}
                disabled={loading}
              >
                Reset Password
              </button>
            </div>

            <div className="login-forgot">
              <span className="login-forgot-text">New program? </span>
              <Link to="/signup" className="login-forgot-link" data-testid="login-signup-link">
                Sign up
              </Link>
            </div>

            {travelBallAvailable && (
              <div className="login-forgot">
                <span className="login-forgot-text">New to travel ball? </span>
                <Link
                  to="/travel-ball/signup"
                  className="login-forgot-link"
                  data-testid="login-travel-ball-signup-link"
                >
                  Sign up
                </Link>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};
